import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { filterPillClass } from '../components/FilterPill';
import { NewRoomDialog } from '../components/room/NewRoomDialog';
import { StatusPill } from '../components/room/StatusPill';
import { pingQuery } from '../lib/api';
import { useMe } from '../lib/auth';
import { pushToast } from '../lib/events';
import { createRoom, roomsQuery, type RoomSummary } from '../lib/rooms';

/** The telemetry strip's left slot. `/api/ping` is admin-only (it leaks the deployed version), so
 * admins see the build stamp and everyone else sees their session — asking for the ping as a plain
 * user would just render a misleading "unreachable". */
function Telemetry() {
  const { data: me } = useMe();
  if (me?.role === 'ADMIN') {
    return <ApiStatus />;
  }
  return (
    <span className="telemetry text-text-muted">
      <span className="mr-2 inline-block size-2 rounded-full bg-accent" aria-hidden="true" />
      Signed in as {me?.username ?? '…'}
    </span>
  );
}

/** Proves the dev proxy (and, in prod, Caddy) actually reach the Boot API on the same origin. */
function ApiStatus() {
  const { data, isPending, isError } = useQuery(pingQuery);

  if (isPending) {
    return <span className="telemetry text-text-faint">Checking API…</span>;
  }
  if (isError) {
    return (
      <span className="telemetry text-signal">
        <span className="mr-2 inline-block size-2 rounded-full bg-signal" aria-hidden="true" />
        API unreachable
      </span>
    );
  }
  return (
    <span className="telemetry text-text-muted">
      <span className="mr-2 inline-block size-2 rounded-full bg-accent" aria-hidden="true" />
      API {data.status} · version {data.version}
    </span>
  );
}

type StatusFilter = 'ALL' | 'LIVE' | 'DRAFT' | 'FINISHED';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'LIVE', label: 'Live' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'FINISHED', label: 'Finished' },
];

interface FilterRowProps {
  statusFilter: StatusFilter;
  onStatusFilter: (filter: StatusFilter) => void;
  showArchived: boolean;
  onToggleArchived: () => void;
}

/** `All · Live · Draft · Finished` eyebrow toggles plus a separate `Archived` toggle — archived
 * rooms stay off the list until that toggle is on, independent of which status tab is active. */
function FilterRow({ statusFilter, onStatusFilter, showArchived, onToggleArchived }: FilterRowProps) {
  return (
    <div className="reveal mt-6 flex flex-wrap items-center gap-2" style={{ ['--i' as string]: 4 }}>
      <div role="group" aria-label="Status filter" className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            aria-pressed={statusFilter === filter.key}
            onClick={() => onStatusFilter(filter.key)}
            className={filterPillClass(statusFilter === filter.key)}
          >
            {filter.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        aria-pressed={showArchived}
        onClick={onToggleArchived}
        className={[
          'eyebrow min-h-tap rounded-full border px-3',
          showArchived ? 'border-text-faint text-text line-through' : 'border-border-strong text-text-faint hover:text-text',
        ].join(' ')}
      >
        Archived
      </button>
    </div>
  );
}

function RoomRow({ room }: { room: RoomSummary }) {
  return (
    <Link
      to={`/rooms/${room.id}`}
      className="grid min-h-tap grid-cols-1 items-center gap-3 rounded-lg border border-border px-5 py-4 transition-colors hover:border-accent-border md:grid-cols-[1fr_auto_auto_auto]"
    >
      <div className="min-w-0">
        <span className="display block truncate text-3xl">{room.className}</span>
        <span className="block truncate text-sm text-text-muted">{room.guestLabel}</span>
      </div>
      <span className="eyebrow hidden md:block">{room.seriesLabel}</span>
      <span className="telemetry hidden text-text-faint md:block">{room.myRole}</span>
      <StatusPill status={room.status} />
    </Link>
  );
}

/** Empty state drawn as the shape of the missing thing: a ghost scoreboard row with the columns
 * the real rows have, and a working "New room" button instead of a disabled placeholder. */
function EmptyRoomsRow({ onNewRoom }: { onNewRoom: () => void }) {
  return (
    <section
      aria-label="Rooms list"
      className="reveal mt-10 grid grid-cols-1 items-center gap-6 rounded-lg border border-dashed border-border-strong px-6 py-8 md:grid-cols-[6rem_1fr_auto_auto]"
      style={{ ['--i' as string]: 4 }}
    >
      <span className="display display-outline hidden text-6xl leading-none md:block" aria-hidden="true">
        S
      </span>
      <div>
        <p className="text-base font-semibold">No rooms yet</p>
        <p className="mt-1 text-sm text-text-faint">
          Host your first interview — invite a guest and start filling in the ratings together.
        </p>
      </div>
      <span className="eyebrow hidden md:block">Guest · —</span>
      <button
        type="button"
        onClick={onNewRoom}
        className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-4 text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-accent-contrast"
      >
        New room
      </button>
    </section>
  );
}

/**
 * The stage. One oversized headline, a telemetry strip with the "New room" action, then the
 * rooms list (scoreboard rows) or the ghost-row empty state.
 */
export function Home() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [showArchived, setShowArchived] = useState(false);
  const { data: rooms, isPending, error } = useQuery(roomsQuery);

  const visibleRooms = (rooms ?? []).filter((room) => {
    if (room.status === 'ARCHIVED' && !showArchived) {
      return false;
    }
    return statusFilter === 'ALL' || room.status === statusFilter;
  });

  const createMutation = useMutation({
    mutationFn: createRoom,
    onSuccess: (snapshot) => {
      void queryClient.invalidateQueries({ queryKey: roomsQuery.queryKey });
      setDialogOpen(false);
      navigate(`/rooms/${snapshot.room.id}`);
    },
    onError: () => pushToast('Could not create the room.'),
  });

  return (
    <div className="mx-auto max-w-6xl">
      <div className="reveal flex items-baseline gap-4" style={{ ['--i' as string]: 0 }}>
        <span className="eyebrow">01 / Lobby</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      <h1 className="display reveal mt-4 text-[clamp(4.5rem,14vw,11rem)]" style={{ ['--i' as string]: 1 }}>
        Rooms
      </h1>

      <p className="reveal mt-4 max-w-prose text-lg text-text-muted" style={{ ['--i' as string]: 2 }}>
        One room per interview. Invite the guest, fill in the ratings together, draw on the board,
        and point OBS at the overlay.
      </p>

      <div
        className="reveal mt-10 flex flex-wrap items-center justify-between gap-4 border-y border-border py-3"
        style={{ ['--i' as string]: 3 }}
      >
        <Telemetry />
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-4 text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-accent-contrast"
        >
          New room
        </button>
      </div>

      {rooms && rooms.length > 0 && (
        <FilterRow
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          showArchived={showArchived}
          onToggleArchived={() => setShowArchived((value) => !value)}
        />
      )}

      {isPending && <p className="reveal telemetry mt-10 text-text-faint">Loading rooms…</p>}
      {error && (
        <p role="alert" className="reveal telemetry mt-10 text-signal">
          Could not load your rooms. Please retry in a moment.
        </p>
      )}

      {rooms && rooms.length === 0 && <EmptyRoomsRow onNewRoom={() => setDialogOpen(true)} />}

      {rooms && rooms.length > 0 && visibleRooms.length === 0 && (
        <p className="reveal mt-10 telemetry text-text-faint">No rooms match these filters.</p>
      )}

      {visibleRooms.length > 0 && (
        <ul className="reveal mt-10 flex flex-col gap-2" style={{ ['--i' as string]: 5 }}>
          {visibleRooms.map((room) => (
            <li key={room.id}>
              <RoomRow room={room} />
            </li>
          ))}
        </ul>
      )}

      {dialogOpen && (
        <NewRoomDialog
          onClose={() => setDialogOpen(false)}
          onSubmit={(input) => createMutation.mutate(input)}
          pending={createMutation.isPending}
        />
      )}
    </div>
  );
}
